import React, { useEffect, useMemo, useState } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import { UserRightsProvider } from './context/UserRightsContext';
import { AppConfigProvider } from './context/AppConfigContext';
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import Dashboard from './components/Dashboard';
import MasterConnector from './components/MasterConnector';
import MasterFlow from './components/MasterFlow';
import MasterLlm from './components/MasterLlm';
import SyncFlow from './components/SyncFlow';
import HubFlow from './components/HubFlow';
import AgentTaskQueue from './agent/AgentTaskQueue';
import AgentAlerts from './agent/AgentAlerts.jsx';
import ConnectorToken from './components/ConnectorToken';
import TenantUsers from './components/TenantUsers';
import GlobalUsers from './components/GlobalUsers';
import TenantAdmin from './components/TenantAdmin';
import LicenseAdmin from './components/LicenseAdmin';
import BillingDashboard from './components/Billing/BillingDashboard';
import BillingAccounts from './components/Billing/BillingAccounts';
import PricingPlans from './components/Billing/PricingPlans';
import BillingSubscriptions from './components/Billing/BillingSubscriptions';
import BillingUsage from './components/Billing/BillingUsage';
import BillingInvoices from './components/Billing/BillingInvoices';
import BillingPayments from './components/Billing/BillingPayments';
import BillingCredits from './components/Billing/BillingCredits';
import BillingRenewals from './components/Billing/BillingRenewals';
import BillingReports from './components/Billing/BillingReports';
import BillingConfig from './components/Billing/BillingConfig';
import TenantBilling from './components/Billing/TenantBilling';
import AppConfig from './components/AppConfig';
import MasterMenusAdmin from './components/MasterMenusAdmin';
import Help from './components/Help';
import CDXTransformation from './components/CDXTransformation';
import TenantTransformation from './components/TenantTransformation';
import { TRANSFORMATION_HEADER_TYPES } from './components/common/transformationHeaderUiConfig';
import Register from './components/Register';
import FileShare from './components/FileShare/FileShare';
import FileShareUsageAdmin from './components/FileShare/FileShareUsageAdmin';
import ShareLinks from './components/FileShare/ShareLinks';
import ChatHub from './components/ChatHub/ChatHub';
import PublicViewer from './components/FileShare/PublicViewer';
import AIAgents from './components/AIAgents';
import PDFHub from './components/PDFHub/PDFHub';
import WebhooksManager from './components/WebhooksManager';
import { isLicenseFeatureAllowed } from './services/licensingApi';
import { ensureMasterMenus, fetchSidebarMenus } from './services/rolesMenuApi';
import WebhookEventRegistryAdmin from './components/webhook/WebhookEventRegistryAdmin';
import GlobalToolsManager from './components/GlobalToolsManager';
import MyIntegrations from './components/MyIntegrations';
import GlobalSchemaAdmin from './components/GlobalSchemaAdmin';
import McpEndpointCatalogAdmin from './components/McpEndpointCatalogAdmin';
import TenantToolEntitlementsAdmin from './components/TenantToolEntitlementsAdmin';
import ModelConfigurationAdmin from './components/ModelConfigurationAdmin';
import DataPointDetails from './components/DataPoint/DataPointDetails';
import GoogleDriveDatapoint from './components/DataPoint/GoogleDriveDatapoint';
import BudgetToItemDatapoint from './components/DataPoint/BudgetToItemDatapoint';
import ContractToPurchaseDatapoint from './components/DataPoint/ContractToPurchaseDatapoint';
import BudgetPaymentDatapoint from './components/DataPoint/BudgetPaymentDatapoint';
import CostPaymentDatapoint from './components/DataPoint/CostPaymentDatapoint';
import ExpenseToExpenseDatapoint from './components/DataPoint/ExpenseToExpenseDatapoint';
import QboExpenseToAutodeskExpenseDatapoint from './components/DataPoint/QboExpenseToAutodeskExpenseDatapoint';
import BudgetMappingScreen from './components/DataPoint/BudgetMappingScreen';
import ViewDataGrid from './components/HubFlowDataPoint/ViewDataGrid';
import IoTDataView from './components/IoT/IoTDataView';
import GlobalLinkRelationship from './components/GlobalLinkRelationship';
import TenantLinkRelationship from './components/TenantLinkRelationship';
import BenchmarkDashboard from './components/Benchmark/BenchmarkDashboard';
import BenchmarkProjects from './components/Benchmark/BenchmarkProjects';
import BenchmarkContracts from './components/Benchmark/BenchmarkContracts';
import BenchmarkCostCategory from './components/Benchmark/BenchmarkCostCategory';
import BenchmarkEstimatedBudgets from './components/Benchmark/BenchmarkEstimatedBudgets';
import BenchmarkActualCost from './components/Benchmark/BenchmarkActualCost';
import BenchmarkChangeOrders from './components/Benchmark/BenchmarkChangeOrders';
import BenchmarkCodeStandard from './components/Benchmark/BenchmarkCodeStandard';
import BenchmarkMain from './components/Benchmark/BenchmarkMain';
import BenchmarkProjectComparison from './components/Benchmark/BenchmarkProjectComparison';
import BenchmarkBidComparison from './components/Benchmark/BenchmarkBidComparison';
import TestAPI from './components/TestAPI/TestAPI';
import TenantRoles from './components/TenantRoles/TenantRoles';
import SessionTimeoutDialog from './components/SessionTimeoutDialog';
import SessionTimeoutTest from './components/SessionTimeoutTest';
import useSessionTimeout from './hooks/useSessionTimeout';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import Login from './components/Login';
import ResetPassword from './components/ResetPassword';
import ForgotPassword from './components/ForgotPassword';
import Profile from './components/Profile';
import CallbackPage from './components/TokenGeneration/CallbackPage';
import { theme } from "./theme";
import { ThemeProvider, CssBaseline } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useUserRights } from './context/UserRightsContext';
import { syncSyncTrackSession } from './services/syncTrackSession';

const CURRENT_USER_ID_URL = `${(process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/').replace(/\/$/, '')}/synctrack/current-user-id`;

/** First navigable path from effective sidebar rows (same order as left nav). */
function firstSidebarPath(menus) {
  if (!Array.isArray(menus) || menus.length === 0) return null;
  const roots = menus
    .filter((m) => m.menuLevel === 1 || !m.parentMenuId)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  const byParent = new Map();
  menus
    .filter((m) => m.parentMenuId)
    .forEach((m) => {
      const key = String(m.parentMenuId);
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key).push(m);
    });
  for (const root of roots) {
    const children = (byParent.get(String(root.menuId)) || []).sort(
      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
    );
    for (const c of children) {
      const path = c.menuPath || c.menu_path;
      if (path) return path;
    }
  }
  return null;
}

// Land on the first sidebar menu this user actually has (products ∩ menus ∩ Access)
const DefaultRedirect = () => {
  const isCdxUser = useIsCdxUser();
  const { rightsLoaded } = useUserRights();
  const [path, setPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const tenantId = useMemo(() => {
    try {
      return localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (isCdxUser) {
      setPath('/dashboard');
      setLoading(false);
      return;
    }
    if (!rightsLoaded) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setFailed(false);
      try {
        if (!tenantId) {
          if (!cancelled) setFailed(true);
          return;
        }
        await ensureMasterMenus().catch(() => null);
        let userId = localStorage.getItem('userId') || localStorage.getItem('user_id');
        if (!userId) {
          const token = localStorage.getItem('token');
          if (token) {
            const res = await fetch(CURRENT_USER_ID_URL, {
              headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
            });
            if (res.ok) {
              const data = await res.json();
              userId = data?.userId ?? data?.user_id ?? null;
              if (userId != null) localStorage.setItem('userId', String(userId));
            }
          }
        }
        if (!userId) {
          if (!cancelled) setFailed(true);
          return;
        }
        const rows = await fetchSidebarMenus(tenantId, {
          includePlatformOnly: false,
          cdxUser: false,
          userId,
        });
        const first = firstSidebarPath(rows);
        if (!cancelled) {
          if (first) setPath(first);
          else setFailed(true);
        }
      } catch (e) {
        console.error('DefaultRedirect sidebar load failed:', e);
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isCdxUser, rightsLoaded, tenantId]);

  if (isCdxUser && path) {
    return <Navigate to={path} replace />;
  }
  if (!rightsLoaded || loading) {
    return <div>Loading permissions...</div>;
  }
  if (path) {
    return <Navigate to={path} replace />;
  }
  if (failed) {
    return <div>No modules available. Please contact your administrator.</div>;
  }
  return <div>Loading permissions...</div>;
};

// Helper function to check CDX user status (re-read each render — do not memoize with [])
const useIsCdxUser = () => {
  try {
    const cdxUser = localStorage.getItem('cdx_user');
    return cdxUser === 'true' || cdxUser === true;
  } catch (error) {
    console.error('Error reading cdx_user from localStorage:', error);
    return false;
  }
};

// Protected component wrapper for CDX-only routes (no rights check)
const ProtectedCdxRoute = ({ children }) => {
  const isCdxUser = useIsCdxUser();

  if (!isCdxUser) {
    return <Navigate to="/home" replace />;
  }

  return children;
};

// Route guard by rights: allow if CDX or Access; product filtering is already in sidebar menus API
const ProtectedByRightsRoute = ({ functionCode, children }) => {
  const { hasFullAccess, canAccessMenu, rightsLoaded } = useUserRights();
  if (hasFullAccess) return children;
  if (!rightsLoaded) return <div>Loading...</div>;
  if (!isLicenseFeatureAllowed(functionCode)) {
    return <Navigate to="/home" replace />;
  }
  if (canAccessMenu(functionCode)) return children;
  return <Navigate to="/home" replace />;
};

// Protected Dashboard component - only for CDX users
const ProtectedDashboard = () => {
  const isCdxUser = useIsCdxUser();

  if (!isCdxUser) {
    return <Navigate to="/home" replace />;
  }

  return <Dashboard />;
};

// Layout component with sidebar
const Layout = ({ children }) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <Header />
      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
        <Sidebar />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            overflowY: "auto",
            backgroundColor: theme.palette.background.default,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

// DataPoint Router - routes to appropriate component based on flow name
const DataPointRouter = () => {
  const location = useLocation();
  const { flowInstanceId } = useParams();
  const { flowName: flowNameFromState, tenantId: tenantIdFromState } = location.state || {};

  const [loading, setLoading] = React.useState(!flowNameFromState);
  const [flowName, setFlowName] = React.useState(flowNameFromState);

  // Fetch flow name if not provided in state (e.g., on page refresh)
  React.useEffect(() => {
    if (!flowName && flowInstanceId) {
      const fetchFlowName = async () => {
        try {
          const token = localStorage.getItem('token');
          // Try to get tenantId from localStorage or use default
          const tenantId = tenantIdFromState || localStorage.getItem('tenantId') || '1'; // fallback to 1 if not available

          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}synctrack/datapoint/flow/${flowInstanceId}/tenant/${tenantId}`,
            {
              method: 'GET',
              headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
            }
          );
          if (response.ok) {
            const config = await response.json();
            setFlowName(config.flowName);
          }
        } catch (error) {
          console.error('Error fetching flow name:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchFlowName();
    } else {
      setLoading(false);
    }
  }, [flowName, flowInstanceId, tenantIdFromState]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div>Loading...</div>
      </Box>
    );
  }

  // Check if this is a Google Drive flow
  // Flow name should contain both "google" and "drive" (case insensitive)
  const isGoogleDriveFlow = flowName &&
    flowName.toLowerCase().includes('google') &&
    flowName.toLowerCase().includes('drive');

  // Budget Payment: check BEFORE Budget to Item — "Autodesk Budget Payment To QuickBooks Invoice" contains "quickbooks"
  const isBudgetPaymentFlow = flowName &&
    flowName.toLowerCase().includes('budget') &&
    (flowName.toLowerCase().includes('payment') || flowName.toLowerCase().includes('invoice'));

  // Budget to Item: budget + item (or quickbooks when it's not payment/invoice flow)
  const isBudgetToItemFlow = flowName &&
    flowName.toLowerCase().includes('budget') &&
    (flowName.toLowerCase().includes('item') || flowName.toLowerCase().includes('quickbooks'));

  // Contract to Purchase Order flow
  const isContractToPurchaseFlow = flowName &&
    flowName.toLowerCase().includes('contract') &&
    flowName.toLowerCase().includes('purchase');

  // Cost Payment: cost + (payment or bill)
  const isCostPaymentFlow = flowName &&
    flowName.toLowerCase().includes('cost') &&
    (flowName.toLowerCase().includes('payment') || flowName.toLowerCase().includes('bill'));

  // QuickBooks Expense → Autodesk Expense: flow name has quickbooks + expense + autodesk, and "quickbooks" comes before "autodesk"
  const flowLower = flowName ? flowName.toLowerCase() : '';
  const isQboExpenseToAutodeskExpenseFlow = flowLower &&
    flowLower.includes('quickbooks') &&
    flowLower.includes('expense') &&
    flowLower.includes('autodesk') &&
    flowLower.indexOf('quickbooks') < flowLower.indexOf('autodesk');

  // Autodesk Expense → QuickBooks Expense: flow name has autodesk + expense + quickbooks, and "autodesk" comes before "quickbooks"
  const isExpenseToExpenseFlow = flowLower &&
    flowLower.includes('autodesk') &&
    flowLower.includes('expense') &&
    flowLower.includes('quickbooks') &&
    flowLower.indexOf('autodesk') < flowLower.indexOf('quickbooks');

  if (isGoogleDriveFlow) {
    return <GoogleDriveDatapoint />;
  }

  if (isBudgetPaymentFlow) {
    return <BudgetPaymentDatapoint />;
  }

  if (isBudgetToItemFlow) {
    return <BudgetToItemDatapoint />;
  }

  if (isContractToPurchaseFlow) {
    return <ContractToPurchaseDatapoint />;
  }

  if (isCostPaymentFlow) {
    return <CostPaymentDatapoint />;
  }

  if (isQboExpenseToAutodeskExpenseFlow) {
    return <QboExpenseToAutodeskExpenseDatapoint />;
  }

  if (isExpenseToExpenseFlow) {
    return <ExpenseToExpenseDatapoint />;
  }

  // Default to DataPointDetails for all other flows
  return <DataPointDetails />;
};

const isJwtValid = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }
  try {
    const parts = token.split('.');
    if (parts.length < 2) {
      return false;
    }
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decoded = JSON.parse(atob(padded));
    if (!decoded.exp || typeof decoded.exp !== 'number') {
      return false;
    }
    return decoded.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

const clearSessionAndRedirect = () => {
  localStorage.clear();
  sessionStorage.clear();
  window.location.replace('/');
};

const RequireAuth = ({ children }) => {
  useEffect(() => {
    const validateSession = () => {
      if (!isJwtValid(localStorage.getItem('token'))) {
        clearSessionAndRedirect();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        validateSession();
      }
    };

    // Keep Connected SyncTrack connectors aligned with current login JWT (Chat Hub / MCP)
    if (isJwtValid(localStorage.getItem('token'))) {
      syncSyncTrackSession();
    }

    window.addEventListener('focus', validateSession);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', validateSession);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  if (!isJwtValid(localStorage.getItem('token'))) {
    clearSessionAndRedirect();
    return null;
  }

  return children;
};

const ProtectedLayout = () => {
  // Initialize session timeout management
  const {
    showWarning,
    timeLeft,
    formatTime,
    handleUserConfirmation,
    handleLogout
  } = useSessionTimeout();

  const { userRights, hasFullAccess, rightsLoaded } = useUserRights();
  // Also trust localStorage in case context mounted before cdx_user was written
  const cdxFromStorage = (() => {
    try {
      return localStorage.getItem('cdx_user') === 'true';
    } catch {
      return false;
    }
  })();
  const hasNoRoles =
    !hasFullAccess &&
    !cdxFromStorage &&
    rightsLoaded &&
    Object.keys(userRights).length === 0;

  const handleNoRoleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/';
  };

  return (
    <Layout>
      {hasNoRoles ? (
        <Dialog open={true} disableEscapeKeyDown fullWidth maxWidth="xs">
          <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>Access Denied</DialogTitle>
          <DialogContent>
            <Typography>No role assigned. Please contact admin.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleNoRoleLogout} variant="contained" color="primary">
              Logout
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        <Routes>
          <Route path="/callback" element={<CallbackPage />} />
          <Route path="/dashboard" element={<ProtectedByRightsRoute functionCode="Dashboard"><Dashboard /></ProtectedByRightsRoute>} />
          <Route path="/master-connector" element={<ProtectedByRightsRoute functionCode="Master Connector"><MasterConnector /></ProtectedByRightsRoute>} />
          <Route path="/master-flow" element={<ProtectedByRightsRoute functionCode="Master Flows"><MasterFlow /></ProtectedByRightsRoute>} />
          <Route path="/master-llm" element={<ProtectedByRightsRoute functionCode="Master LLM"><MasterLlm /></ProtectedByRightsRoute>} />
          <Route
            path="/transformation"
            element={
              <ProtectedByRightsRoute functionCode="Transformation">
                <TenantTransformation
                  fixedType={TRANSFORMATION_HEADER_TYPES.SYNC_FLOW}
                  title="Transformation"
                  propertiesTitle="Transformation Properties"
                  functionCode="Transformation"
                />
              </ProtectedByRightsRoute>
            }
          />
          <Route
            path="/data-schema"
            element={
              <ProtectedByRightsRoute functionCode="Data Schema">
                <TenantTransformation
                  fixedType={TRANSFORMATION_HEADER_TYPES.DATA_HUB}
                  title="Data Schema"
                  propertiesTitle="Data Schema Properties"
                  functionCode="Data Schema"
                />
              </ProtectedByRightsRoute>
            }
          />
          <Route
            path="/global-transformation"
            element={
              <ProtectedByRightsRoute functionCode="Global Transformation">
                <CDXTransformation
                  fixedType={TRANSFORMATION_HEADER_TYPES.SYNC_FLOW}
                  title="Global Transformation"
                  propertiesTitle="Global Transformation Properties"
                  functionCode="Global Transformation"
                />
              </ProtectedByRightsRoute>
            }
          />
          <Route
            path="/global-data-schema"
            element={
              <ProtectedByRightsRoute functionCode="Global Data Schema">
                <CDXTransformation
                  fixedType={TRANSFORMATION_HEADER_TYPES.DATA_HUB}
                  title="Global Data Schema"
                  propertiesTitle="Global Data Schema Properties"
                  functionCode="Global Data Schema"
                />
              </ProtectedByRightsRoute>
            }
          />
          {/* Legacy redirects — old combined screens */}
          <Route path="/tenant-transformation" element={<Navigate to="/data-schema" replace />} />
          <Route path="/cdx-transformation" element={<Navigate to="/global-data-schema" replace />} />
          <Route path="/file-share" element={<ProtectedByRightsRoute functionCode="Design Share"><FileShare /></ProtectedByRightsRoute>} />
          <Route path="/file-share/share-links" element={<ProtectedByRightsRoute functionCode="Design Share"><ShareLinks /></ProtectedByRightsRoute>} />
          <Route path="/file-share/projects/:projectId" element={<ProtectedByRightsRoute functionCode="Design Share"><FileShare /></ProtectedByRightsRoute>} />
          <Route path="/file-share/projects/:projectId/files/:fileId" element={<ProtectedByRightsRoute functionCode="Design Share"><FileShare /></ProtectedByRightsRoute>} />
          <Route path="/admin/file-share-usage" element={<ProtectedByRightsRoute functionCode="File Share Usage"><FileShareUsageAdmin /></ProtectedByRightsRoute>} />
          <Route path="/sync-flow" element={<ProtectedByRightsRoute functionCode="Sync Flow"><SyncFlow /></ProtectedByRightsRoute>} />
          <Route path="/webhooks" element={<ProtectedByRightsRoute functionCode="Webhook"><WebhooksManager /></ProtectedByRightsRoute>} />
          <Route path="/hub-flow" element={<ProtectedByRightsRoute functionCode="Hub Flow"><HubFlow /></ProtectedByRightsRoute>} />
          <Route path="/hubflow-datapoint" element={<ProtectedByRightsRoute functionCode="Data Point"><ViewDataGrid /></ProtectedByRightsRoute>} />
          <Route path="/iot/data-view" element={<ProtectedByRightsRoute functionCode="IoT Data View"><IoTDataView /></ProtectedByRightsRoute>} />
          <Route path="/global-link-relationships" element={<ProtectedByRightsRoute functionCode="Global Link Relationships"><GlobalLinkRelationship /></ProtectedByRightsRoute>} />
          <Route path="/tenant-link-relationships" element={<ProtectedByRightsRoute functionCode="Global Link Relationships"><TenantLinkRelationship /></ProtectedByRightsRoute>} />
          <Route path="/datapoint/:flowInstanceId" element={<ProtectedByRightsRoute functionCode="Data Point"><DataPointRouter /></ProtectedByRightsRoute>} />
          <Route path="/datapoint/:flowInstanceId/mapping" element={<ProtectedByRightsRoute functionCode="Data Point"><BudgetMappingScreen /></ProtectedByRightsRoute>} />
          <Route path="/connector-token" element={<ProtectedByRightsRoute functionCode="Connector"><ConnectorToken /></ProtectedByRightsRoute>} />
          <Route path="/ai-agents" element={<ProtectedByRightsRoute functionCode="Agent Setup"><AIAgents /></ProtectedByRightsRoute>} />
          <Route path="/admin/model-configuration" element={<ProtectedByRightsRoute functionCode="Model Configuration"><ModelConfigurationAdmin /></ProtectedByRightsRoute>} />
          <Route path="/agent-task-queue" element={<ProtectedByRightsRoute functionCode="Task Queue"><AgentTaskQueue /></ProtectedByRightsRoute>} />
          <Route path="/agent-alerts" element={<ProtectedByRightsRoute functionCode="Agent Setup"><AgentAlerts /></ProtectedByRightsRoute>} />
          <Route path="/admin/global-tools" element={<ProtectedByRightsRoute functionCode="Global Tools"><GlobalToolsManager /></ProtectedByRightsRoute>} />
          <Route path="/admin/webhook-event-registry" element={<ProtectedByRightsRoute functionCode="Webhook Event Registry"><WebhookEventRegistryAdmin /></ProtectedByRightsRoute>} />
          <Route path="/admin/mcp-endpoint-catalog" element={<ProtectedByRightsRoute functionCode="Global Tools"><McpEndpointCatalogAdmin /></ProtectedByRightsRoute>} />
          <Route path="/admin/tenant-tools" element={<ProtectedByRightsRoute functionCode="Agent Tools"><TenantToolEntitlementsAdmin /></ProtectedByRightsRoute>} />
          <Route path="/admin/tenant-tool-entitlements" element={<Navigate to="/admin/tenant-tools" replace />} />
          <Route path="/admin/mcp-endpoint-tenant" element={<Navigate to="/admin/tenant-tools" replace />} />
          <Route path="/my-integrations" element={<ProtectedByRightsRoute functionCode="Integration"><MyIntegrations /></ProtectedByRightsRoute>} />
          <Route path="/admin/global-schema" element={<ProtectedByRightsRoute functionCode="AI Global Schema"><GlobalSchemaAdmin /></ProtectedByRightsRoute>} />
          <Route path="/chat-hub" element={<ProtectedByRightsRoute functionCode="Chat Hub"><ChatHub /></ProtectedByRightsRoute>} />
          <Route path="/pdf-hub" element={<ProtectedByRightsRoute functionCode="PDF Hub"><PDFHub /></ProtectedByRightsRoute>} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/tenant" element={<ProtectedByRightsRoute functionCode="Tenant"><TenantAdmin /></ProtectedByRightsRoute>} />
          <Route path="/tenant-users" element={<ProtectedByRightsRoute functionCode="Users"><TenantUsers /></ProtectedByRightsRoute>} />
          <Route path="/admin/global-users" element={<ProtectedByRightsRoute functionCode="Global Users"><GlobalUsers /></ProtectedByRightsRoute>} />
          <Route path="/admin/license" element={<ProtectedCdxRoute><LicenseAdmin /></ProtectedCdxRoute>} />
          <Route path="/admin/billing" element={<ProtectedCdxRoute><BillingDashboard /></ProtectedCdxRoute>} />
          <Route path="/admin/billing/accounts" element={<ProtectedCdxRoute><BillingAccounts /></ProtectedCdxRoute>} />
          <Route path="/admin/billing/subscriptions" element={<ProtectedCdxRoute><BillingSubscriptions /></ProtectedCdxRoute>} />
          <Route path="/admin/billing/pricing" element={<ProtectedCdxRoute><PricingPlans /></ProtectedCdxRoute>} />
          <Route path="/admin/billing/usage" element={<ProtectedCdxRoute><BillingUsage /></ProtectedCdxRoute>} />
          <Route path="/admin/billing/invoices" element={<ProtectedCdxRoute><BillingInvoices /></ProtectedCdxRoute>} />
          <Route path="/admin/billing/payments" element={<ProtectedCdxRoute><BillingPayments /></ProtectedCdxRoute>} />
          <Route path="/admin/billing/credits" element={<ProtectedCdxRoute><BillingCredits /></ProtectedCdxRoute>} />
          <Route path="/admin/billing/renewals" element={<ProtectedCdxRoute><BillingRenewals /></ProtectedCdxRoute>} />
          <Route path="/admin/billing/reports" element={<ProtectedCdxRoute><BillingReports /></ProtectedCdxRoute>} />
          <Route path="/admin/billing/config" element={<ProtectedCdxRoute><BillingConfig /></ProtectedCdxRoute>} />
          <Route path="/billing-subscription" element={<ProtectedByRightsRoute functionCode="Billing & Subscription"><TenantBilling /></ProtectedByRightsRoute>} />
          <Route path="/app-config" element={<ProtectedByRightsRoute functionCode="App Config"><AppConfig /></ProtectedByRightsRoute>} />
          <Route path="/admin/master-menus" element={<ProtectedByRightsRoute functionCode="Master Menus"><MasterMenusAdmin /></ProtectedByRightsRoute>} />
          <Route path="/help" element={<ProtectedByRightsRoute functionCode="Help"><Help /></ProtectedByRightsRoute>} />
          <Route path="/tenant-roles" element={<ProtectedByRightsRoute functionCode="Roles"><TenantRoles /></ProtectedByRightsRoute>} />
          <Route path="/test-api" element={<ProtectedByRightsRoute functionCode="Test API"><TestAPI /></ProtectedByRightsRoute>} />
          <Route path="/session-test" element={<SessionTimeoutTest />} />
          <Route path="/benchmark/dashboard" element={<ProtectedByRightsRoute functionCode="Benchmark Hub"><BenchmarkDashboard /></ProtectedByRightsRoute>} />
          <Route path="/benchmark/projects" element={<ProtectedByRightsRoute functionCode="Projects"><BenchmarkProjects /></ProtectedByRightsRoute>} />
          <Route path="/benchmark/contracts/:projectId" element={<ProtectedByRightsRoute functionCode="Projects"><BenchmarkContracts /></ProtectedByRightsRoute>} />
          <Route path="/benchmark/cost-category/:projectId" element={<ProtectedByRightsRoute functionCode="Projects"><BenchmarkCostCategory /></ProtectedByRightsRoute>} />
          <Route path="/benchmark/estimated-budgets/:contractId" element={<ProtectedByRightsRoute functionCode="Projects"><BenchmarkEstimatedBudgets /></ProtectedByRightsRoute>} />
          <Route path="/benchmark/actual-cost/:contractId" element={<ProtectedByRightsRoute functionCode="Projects"><BenchmarkActualCost /></ProtectedByRightsRoute>} />
          <Route path="/benchmark/change-orders/:contractId" element={<ProtectedByRightsRoute functionCode="Projects"><BenchmarkChangeOrders /></ProtectedByRightsRoute>} />
          <Route path="/benchmark/code-standard" element={<ProtectedByRightsRoute functionCode="Cost Structure"><BenchmarkCodeStandard /></ProtectedByRightsRoute>} />
          <Route path="/benchmark/benchmark" element={<ProtectedByRightsRoute functionCode="Cost Benchmark"><BenchmarkMain /></ProtectedByRightsRoute>} />
          <Route path="/benchmark/comparison" element={<ProtectedByRightsRoute functionCode="Project Comparison"><BenchmarkProjectComparison /></ProtectedByRightsRoute>} />
          <Route path="/benchmark/bid-comparison" element={<ProtectedByRightsRoute functionCode="Bid Comparison"><BenchmarkBidComparison /></ProtectedByRightsRoute>} />
          <Route path="/home" element={<DefaultRedirect />} />
          <Route path="/*" element={<DefaultRedirect />} />
        </Routes>
      )}

      {/* Session Timeout Dialog */}
      <SessionTimeoutDialog
        open={showWarning}
        timeLeft={timeLeft}
        formatTime={formatTime}
        onConfirm={handleUserConfirmation}
        onLogout={handleLogout}
      />
    </Layout>
  );
};

// App component with proper routing
const App = () => {
  // 🎯 FRONTEND CI/CD TEST: Testing complete frontend pipeline
  console.log('🎯 FRONTEND CI/CD TEST: App component loaded - Testing dev branch deployment!');

  return (
    <Router>
      <ThemeProvider theme={theme}>
        <UserRightsProvider>
          <AppConfigProvider>
          <CssBaseline />
          <Routes>
            {/* Public routes - no authentication required */}
            <Route path="/" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/sign-up" element={<Register />} />
            <Route path="/register" element={<Navigate to="/sign-up" replace />} />
            <Route path="/viewer/public/:shareId" element={<PublicViewer />} />

            {/* Protected routes */}
            <Route path="/*" element={<RequireAuth><ProtectedLayout /></RequireAuth>} />
          </Routes>
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
          </AppConfigProvider>
        </UserRightsProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;